import { Button, Checkbox, Flex, Input } from "antd";
import { useEffect, useState } from "react";
import CheckCircleIcon from "../../../../../assets/icons/CheckCircleIcon";
import CloseIcon from "../../../../../assets/icons/CloseIcon";
import ConfirmModal, {
  ConfirmModalIconType,
} from "../../../../../components/ui/ConfirmModal";
import {
  useApproveInvoiceMutation,
  useRejectInvoiceMutation,
} from "../../../../../features/Invoice/invoice.api";
import useError from "../../../../../hooks/useError";
import useModal from "../../../../../hooks/useModal";
import { toast } from "../../../../../lib/toast";

type ModalType = "REJECT" | "APPROVE" | "APPROVE-PAY" | "SUBMIT";
type ModalConfig = {
  question: string;
  title: string;
  label: string;
  icon: ConfirmModalIconType;
  okText: string;
};

type TConfig = {
  isConfirmed: boolean;
  comment: string;
};

const WaitingForApprovalActions = ({
  title,
  id,
  handleClose,
}: {
  title: string;
  id: number;
  handleClose: ((val?: any) => void) | undefined;
}) => {
  const [config, setConfig] = useState<TConfig>({
    isConfirmed: false,
    comment: "",
  });

  const {
    open: actionOpen,
    handleOpen: handleActionOpen,
    handleClose: handleActionClose,
  } = useModal<unknown, ModalType>();

  // ------ Mutations ------
  const [
    approveInvoice,
    {
      isLoading: approving,
      isSuccess: approved,
      isError: isApproveError,
      error: approveError,
    },
  ] = useApproveInvoiceMutation();
  const [
    rejectInvoice,
    {
      isLoading: rejecting,
      isSuccess: rejected,
      isError: isRejectError,
      error: rejectError,
    },
  ] = useRejectInvoiceMutation();

  const handleAction = () => {
    if (actionOpen.type === "APPROVE") {
      if (config.isConfirmed) {
        approveInvoice({ id, data: { comments: config.comment || "" } });
      } else {
        toast("error", "Please confirm first.");
      }
    } else if (actionOpen.type === "REJECT") {
      if (config.isConfirmed) {
        rejectInvoice({ id, data: { comments: config.comment || "" } });
      } else {
        toast("error", "Please confirm first.");
      }
    }
  };

  // ------ Effects ------
  useEffect(() => {
    if (approved) {
      toast("success", "Invoice successfully approved.");
      handleClose && handleClose();
      handleActionClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [approved]);

  useEffect(() => {
    if (rejected) {
      toast("success", "Invoice successfully rejected.");
      handleClose && handleClose();
      handleActionClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rejected]);

  // ------ Errors ------
  useError(isApproveError, approveError, "Invoice approve failed.");
  useError(isRejectError, rejectError, "Invoice reject failed.");

  // ------ Function for generate config based on modal type ------
  const generateModalConfig = (): ModalConfig | undefined => {
    switch (actionOpen.type) {
      case "REJECT":
        return {
          title,
          icon: "DELETE",
          label: "Reject Note",
          okText: "Yes, Reject",
          question: "Are you sure your want to reject this request?",
        };
      case "APPROVE":
        return {
          title,
          icon: "CHECK_CIRCLE",
          label: "Approve Note",
          okText: "Yes, Approve",
          question: "Are you sure you want to approve this request?",
        };
    }
  };
  const modalConfig = generateModalConfig();

  return (
    <div>
      <Flex justify="end" gap={8}>
        <Button
          onClick={() => handleActionOpen({ type: "REJECT" })}
          size="small"
          className="button-error"
        >
          <CloseIcon height={10} width={10} /> Reject
        </Button>
        <Button
          onClick={() => handleActionOpen({ type: "APPROVE" })}
          size="small"
          className="button-success"
        >
          <CheckCircleIcon height={18} width={18} /> Approve
        </Button>
      </Flex>
      <ConfirmModal
        closable
        icon={modalConfig?.icon || "CHECK_CIRCLE"}
        okText={modalConfig?.okText}
        confirmTitle={
          <>
            <span className="block">{modalConfig?.question}</span>
            <span className="block text-sm text-theme-gray-700 font-normal">
              {modalConfig?.title}
            </span>
          </>
        }
        questionText={""}
        open={actionOpen.open}
        handleClose={handleActionClose}
        handleOk={handleAction}
        okButtonProps={{
          className:
            modalConfig?.icon === "DELETE" ? "button-error" : "button-success",
          disabled: !config.isConfirmed,
        }}
        okLoading={approving || rejecting}
      >
        <span className="block mb-2 text-sm text-theme-gray-900 font-medium">
          {modalConfig?.label}
        </span>
        <Input
          onChange={(e) =>
            setConfig((prev) => ({ ...prev, comment: e.currentTarget.value }))
          }
          className="bg-input"
          placeholder="Enter reason"
        />
        <Checkbox
          onChange={(e) =>
            setConfig((prev) => ({ ...prev, isConfirmed: e.target.checked }))
          }
          className="mt-4"
        >
          Yes, I confirmed!
        </Checkbox>
      </ConfirmModal>
    </div>
  );
};

export default WaitingForApprovalActions;
